import {
  useState,
  useCallback,
  useEffect,
  useRef,
  type Dispatch,
  type SetStateAction,
} from "react";

interface UseHtsMerchandisingScriptsParams {
  isHtsCfar: boolean;
  isHtsDisruption: boolean;
  isCfarAvailable: boolean;
  isDisruptionProtectionAvailable: boolean;
  resetCfarSelection: () => void;
  resetDisruptionSelection: () => void;
}

interface UseHtsMerchandisingScriptsResult {
  isHtsFlexibilityLoaded: boolean;
  htsCfarComplete: boolean;
  htsDisruptionComplete: boolean;
  htsLoadingError: boolean;
  htsCfarLoadingError: boolean;
  htsDisruptionLoadingError: boolean;
  htsCfarRetryCount: number;
  htsDisruptionRetryCount: number;
  setHtsCfarLoaded: Dispatch<SetStateAction<boolean>>;
  setHtsDisruptionLoaded: Dispatch<SetStateAction<boolean>>;
  onHtsCfarLoadSuccess: () => void;
  onHtsDisruptionLoadSuccess: () => void;
  onHtsCfarLoadError: () => void;
  onHtsDisruptionLoadError: () => void;
  onHtsCfarRetry: () => void;
  onHtsDisruptionRetry: () => void;
}

export const useHtsMerchandisingScripts = ({
  isHtsCfar,
  isHtsDisruption,
  isCfarAvailable,
  isDisruptionProtectionAvailable,
  resetCfarSelection,
  resetDisruptionSelection,
}: UseHtsMerchandisingScriptsParams): UseHtsMerchandisingScriptsResult => {
  const [htsCfarLoaded, setHtsCfarLoaded] = useState(false);
  const [htsDisruptionLoaded, setHtsDisruptionLoaded] = useState(false);
  const [htsCfarError, setHtsCfarError] = useState(false);
  const [htsDisruptionError, setHtsDisruptionError] = useState(false);
  const [htsCfarRetryCount, setHtsCfarRetryCount] = useState(0);
  const [htsDisruptionRetryCount, setHtsDisruptionRetryCount] = useState(0);

  const isMountedRef = useRef(true);
  const cfarTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const disruptionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  useEffect(
    () => () => {
      isMountedRef.current = false;
      if (cfarTimeoutRef.current) {
        clearTimeout(cfarTimeoutRef.current);
      }
      if (disruptionTimeoutRef.current) {
        clearTimeout(disruptionTimeoutRef.current);
      }
    },
    []
  );

  const onHtsCfarLoadSuccess = useCallback(() => {
    if (cfarTimeoutRef.current) {
      clearTimeout(cfarTimeoutRef.current);
    }

    cfarTimeoutRef.current = setTimeout(() => {
      if (isMountedRef.current) {
        setHtsCfarLoaded(true);
      }
      cfarTimeoutRef.current = null;
    }, 500);
  }, []);

  const onHtsDisruptionLoadSuccess = useCallback(() => {
    if (disruptionTimeoutRef.current) {
      clearTimeout(disruptionTimeoutRef.current);
    }

    disruptionTimeoutRef.current = setTimeout(() => {
      if (isMountedRef.current) {
        setHtsDisruptionLoaded(true);
      }
      disruptionTimeoutRef.current = null;
    }, 500);
  }, []);

  const onHtsCfarRetry = useCallback(() => {
    resetCfarSelection();
    setHtsCfarError(false);
    setHtsCfarRetryCount((prevCount) => prevCount + 1);
  }, [resetCfarSelection]);

  const onHtsDisruptionRetry = useCallback(() => {
    resetDisruptionSelection();
    setHtsDisruptionError(false);
    setHtsDisruptionRetryCount((prevCount) => prevCount + 1);
  }, [resetDisruptionSelection]);

  const htsCfarComplete = !isHtsCfar || htsCfarLoaded || htsCfarError;
  const htsDisruptionComplete =
    !isHtsDisruption || htsDisruptionLoaded || htsDisruptionError;

  const isHtsFlexibilityLoaded = htsCfarComplete && htsDisruptionComplete;

  const onHtsCfarLoadError = useCallback(() => {
    if (cfarTimeoutRef.current) {
      clearTimeout(cfarTimeoutRef.current);
    }

    cfarTimeoutRef.current = setTimeout(() => {
      if (isMountedRef.current) {
        setHtsCfarError(true);
      }
      cfarTimeoutRef.current = null;
    }, 500);
  }, []);

  const onHtsDisruptionLoadError = useCallback(() => {
    if (disruptionTimeoutRef.current) {
      clearTimeout(disruptionTimeoutRef.current);
    }

    disruptionTimeoutRef.current = setTimeout(() => {
      if (isMountedRef.current) {
        setHtsDisruptionError(true);
      }
      disruptionTimeoutRef.current = null;
    }, 500);
  }, []);

  const onlyHtsCfarAvailableAndFailed =
    isHtsCfar && htsCfarError && !isDisruptionProtectionAvailable;

  const onlyHtsDisruptionAvailableAndFailed =
    isHtsDisruption && htsDisruptionError && !isCfarAvailable;

  const bothHtsProductsFailed =
    isHtsCfar && isHtsDisruption && htsCfarError && htsDisruptionError;

  const htsLoadingError =
    onlyHtsCfarAvailableAndFailed ||
    onlyHtsDisruptionAvailableAndFailed ||
    bothHtsProductsFailed;

  const htsCfarLoadingError = isHtsCfar && htsCfarError;
  const htsDisruptionLoadingError = isHtsDisruption && htsDisruptionError;

  return {
    htsCfarComplete,
    htsDisruptionComplete,
    isHtsFlexibilityLoaded,
    htsLoadingError,
    htsCfarLoadingError,
    htsDisruptionLoadingError,
    htsCfarRetryCount,
    htsDisruptionRetryCount,
    setHtsCfarLoaded,
    setHtsDisruptionLoaded,
    onHtsCfarLoadSuccess,
    onHtsDisruptionLoadSuccess,
    onHtsCfarLoadError,
    onHtsDisruptionLoadError,
    onHtsCfarRetry,
    onHtsDisruptionRetry,
  };
};
